import React, { useMemo, useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { MDBContainer } from "mdb-react-ui-kit";
import { usePageState } from "../context/AppStateContext";
import { useAuth } from "../context/AuthContext";
import { formatDisplayPrice } from "../utils/displayPrice";
import { normalizeRole } from "../utils/rolePermissions";
import api from "../services/api";
import "./ViewProductsPage.css";

const marketplaces = [
  { id: "ebay", name: "eBay", color: "primary" },
  { id: "shopify", name: "Shopify", color: "success" },
  { id: "poshmark", name: "Poshmark", color: "secondary" },
];

const getUserDisplayName = (u) => {
  if (!u) return "";
  const fullName = [u.firstName, u.lastName].filter(Boolean).join(" ").trim();
  return u.name || fullName || u.email || "";
};

const resolveUploadedByName = (product, currentUser) => {
  const fromApi =
    product.uploadedBy?.name ||
    product.uploaded_by_name ||
    getUserDisplayName(product.uploadedBy);

  if (fromApi) return fromApi;

  const uploadedById = product.uploadedBy?.id ?? product.uploaded_by;
  const currentUserId =
    currentUser?.id || currentUser?.user_id || currentUser?.userId;

  if (
    uploadedById != null &&
    currentUserId != null &&
    String(uploadedById) === String(currentUserId)
  ) {
    return getUserDisplayName(currentUser) || "You";
  }

  return "";
};

const ViewProductsPage = () => {
  const navigate = useNavigate();
  const { displayPricePercentage, user, companyId } = useAuth();
  const userId = user?.id || user?.user_id || user?.userId;
  const isOwnerOrAdmin = ["owner", "admin"].includes(normalizeRole(user?.role));

  const { state, setState, clearState } = usePageState("viewProducts");

  // Destructure state with caching fields
  const {
    currentPage = 1,
    itemsPerPage = 5,
    searchQuery = "",
    categoryFilter = "",
    marketplaceFilter = "",
    sellerFilter = "",
    selectedProductIds = [],
    expandedProduct = null,
    cachedProducts = null,
    lastFetched = null,
  } = state;

  const [products, setProducts] = useState(cachedProducts || []);
  const [loading, setLoading] = useState(true);

  // Fetch products with caching
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchProducts = useCallback(async (force = false) => {
    const now = Date.now();
    const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

    if (!force && cachedProducts && lastFetched && (now - lastFetched) < CACHE_TTL) {
      setProducts(cachedProducts);
      setLoading(false);
      return;
    }

    setLoading(true);

    // Helper – uses latest isOwnerOrAdmin & displayPricePercentage (in closure)
    const formatPriceInner = (amount, currencyPrefix = "$") =>
      isOwnerOrAdmin
        ? `${currencyPrefix}${Number(amount || 0).toFixed(2)}`
        : formatDisplayPrice(amount, displayPricePercentage, {
            prefix: currencyPrefix,
          });

    try {
      const { data: productsRes } = await api.get("/api/products", {
        params: {
          company_id: companyId,
          user_id: userId,
        },
      });

      const productsData = Array.isArray(productsRes?.data)
        ? productsRes.data
        : productsRes?.data?.products || productsRes?.products || [];

      const productsWithMarketplaces = await Promise.all(
        productsData.map(async (product) => {
          let ebayData = null;
          let shopifyData = null;

          try {
            const res = await api.get(`/api/ebay-product-by-product/${product.id}`);
            ebayData = res.data?.data;
          } catch {
            // ignore
          }

          try {
            const res = await api.get(`/api/shopify-product/${product.id}`);
            shopifyData = res.data?.data;
          } catch {
            // ignore
          }

          const hasEbay = ebayData && Object.keys(ebayData).length > 0;
          const hasShopify = shopifyData && Object.keys(shopifyData).length > 0;

          const marketplaceDetails = {};

          const categoryName = product.category?.name || product.category || "Other";
          const uploadedByName = resolveUploadedByName(product, user);
          const conditionValue =
            product.condition ||
            product.condition_name ||
            product.item_condition ||
            ebayData?.condition ||
            null;

          marketplaceDetails.local = {
            Title: product.title || "",
            Description: product.description || "",
            SKU: product.sku || "",
            Price: formatPriceInner(product.price || 0),
            Quantity: product.quantity || 0,
            Category: categoryName,
            Condition: conditionValue || "Not specified",
            ...(uploadedByName ? { "Uploaded By": uploadedByName } : {}),
          };

          if (hasEbay) {
            const ebayFields = {};

            if (ebayData.aspects) {
              Object.entries(ebayData.aspects).forEach(([key, value]) => {
                const val = Array.isArray(value) ? value.join(", ") : value;
                if (val && val !== "N/A") ebayFields[key] = val;
              });
            }

            ebayFields["eBay Item ID"] = ebayData.ebay_item_id || "Not listed";
            ebayFields.SKU = ebayData.sku || product.sku || "";
            ebayFields.Price = formatPriceInner(
              ebayData.price || product.price || 0,
              ebayData.currency || "$"
            );
            ebayFields.Quantity = ebayData.quantity || 0;
            ebayFields.Condition = ebayData.condition || product.condition || "";
            ebayFields["Listing Type"] = ebayData.listing_type || "Not specified";
            ebayFields.Duration = ebayData.duration || "Not specified";

            marketplaceDetails.ebay = ebayFields;
          }

          if (hasShopify) {
            const shopifyFields = {};

            shopifyFields["Shopify Product ID"] = shopifyData.shopify_product_id || "Not synced";
            shopifyFields["Variant ID"] = shopifyData.shopify_variant_id || "Not synced";
            shopifyFields["Inventory Item ID"] = shopifyData.inventory_item_id || "Not synced";
            shopifyFields.SKU = shopifyData.sku || product.sku || "";
            shopifyFields.Price = formatPriceInner(shopifyData.price || product.price || 0);
            shopifyFields.Quantity = shopifyData.quantity || 0;
            shopifyFields.Vendor = shopifyData.vendor || "";
            shopifyFields["Product Type"] = shopifyData.product_type || product.category || "";
            shopifyFields.Status = shopifyData.status || "draft";

            marketplaceDetails.shopify = shopifyFields;
          }

          return {
            id: product.id,
            title: product.title,
            description: product.description,
            price: product.price,
            category: categoryName,
            condition: conditionValue,
            quantity: product.quantity,
            sku: product.sku,
            uploadedBy: {
              id: product.uploadedBy?.id ?? product.uploaded_by ?? null,
              name: uploadedByName,
            },
            images: product.images?.length
              ? product.images
              : product.primaryImage
              ? [product.primaryImage]
              : ["https://via.placeholder.com/120"],
            selectedMarketplaces: [
              "local",
              ...(hasEbay ? ["ebay"] : []),
              ...(hasShopify ? ["shopify"] : []),
            ],
            marketplaceDetails,
          };
        })
      );

      setProducts(productsWithMarketplaces);
      setState({
        cachedProducts: productsWithMarketplaces,
        lastFetched: Date.now(),
      });
    } catch (error) {
      console.error("API ERROR:", error);
    } finally {
      setLoading(false);
    }
  }, [companyId, userId, user, cachedProducts, lastFetched, setState, isOwnerOrAdmin, displayPricePercentage]);

  useEffect(() => {
    if (userId && companyId) {
      fetchProducts();
    } else {
      setLoading(false);
    }
  }, [userId, companyId, fetchProducts]);

  const categories = useMemo(
    () => [...new Set(products.map((p) => p.category).filter(Boolean))],
    [products]
  );

  const sellers = useMemo(
    () => [...new Set(products.map((p) => p.uploadedBy?.name).filter(Boolean))],
    [products]
  );

  const filteredProducts = useMemo(() => {
    return products.filter((product) => {
      const matchesSearch =
        searchQuery === "" ||
        product.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.description?.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesCategory = categoryFilter === "" || product.category === categoryFilter;
      const matchesMarketplace =
        marketplaceFilter === "" || product.selectedMarketplaces.includes(marketplaceFilter);
      const matchesSeller = sellerFilter === "" || product.uploadedBy?.name === sellerFilter;

      return matchesSearch && matchesCategory && matchesMarketplace && matchesSeller;
    });
  }, [products, searchQuery, categoryFilter, marketplaceFilter, sellerFilter]);

  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedProducts = filteredProducts.slice(startIndex, startIndex + itemsPerPage);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setState({ currentPage: page });
  };

  const handleSelectProduct = (productId) => {
    const isSelected = selectedProductIds.includes(productId);
    const newSelection = isSelected
      ? selectedProductIds.filter((id) => id !== productId)
      : [...selectedProductIds, productId];
    setState({ selectedProductIds: newSelection });
  };

  const handleSelectAll = () => {
    const allIds = paginatedProducts.map((p) => p.id);
    const allSelected = allIds.every((id) => selectedProductIds.includes(id));

    if (allSelected) {
      setState({ selectedProductIds: selectedProductIds.filter((id) => !allIds.includes(id)) });
    } else {
      setState({ selectedProductIds: [...new Set([...selectedProductIds, ...allIds])] });
    }
  };

  const handleToggleExpand = (productId) => {
    setState({ expandedProduct: expandedProduct === productId ? null : productId });
  };

  const handleClearFilters = () => {
    clearState();
  };

  if (loading) {
    return (
      <MDBContainer className="py-5 text-center">
        <h4>Loading products...</h4>
      </MDBContainer>
    );
  }

  return (
    <MDBContainer fluid className="view-products-container">
      <h2 className="mb-4">View Products</h2>

      <div className="filters-section">
        <div className="filters-grid">
          <div className="filter-item">
            <label>Search</label>
            <input
              type="text"
              className="filter-input"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setState({ searchQuery: e.target.value, currentPage: 1 })}
            />
          </div>

          <div className="filter-item">
            <label>Category</label>
            <select
              className="filter-select"
              value={categoryFilter}
              onChange={(e) => setState({ categoryFilter: e.target.value, currentPage: 1 })}
            >
              <option value="">All Categories</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-item">
            <label>Marketplace</label>
            <select
              className="filter-select"
              value={marketplaceFilter}
              onChange={(e) => setState({ marketplaceFilter: e.target.value, currentPage: 1 })}
            >
              <option value="">All Marketplaces</option>
              {marketplaces.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-item">
            <label>Seller</label>
            <select
              className="filter-select"
              value={sellerFilter}
              onChange={(e) => setState({ sellerFilter: e.target.value, currentPage: 1 })}
            >
              <option value="">All Sellers</option>
              {sellers.map((seller) => (
                <option key={seller} value={seller}>
                  {seller}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-item">
            <label>Items per page</label>
            <select
              className="filter-select"
              value={itemsPerPage}
              onChange={(e) =>
                setState({ itemsPerPage: parseInt(e.target.value), currentPage: 1 })
              }
            >
              <option value={5}>5 per page</option>
              <option value={10}>10 per page</option>
              <option value={20}>20 per page</option>
            </select>
          </div>

          <div className="filter-item">
            <button className="btn btn-outline-primary me-2" onClick={() => fetchProducts(true)}>
              Refresh
            </button>
            <button className="btn btn-secondary clear-filters-btn" onClick={handleClearFilters}>
              Clear Filters
            </button>
          </div>
        </div>

        {(searchQuery || categoryFilter || marketplaceFilter || sellerFilter) && (
          <div className="active-filters">
            <small className="text-muted">
              Showing {filteredProducts.length} of {products.length} products
            </small>
            {searchQuery && <span className="filter-badge">Search: "{searchQuery}"</span>}
            {categoryFilter && <span className="filter-badge">Category: {categoryFilter}</span>}
            {marketplaceFilter && (
              <span className="filter-badge">Marketplace: {marketplaceFilter}</span>
            )}
            {sellerFilter && <span className="filter-badge">Seller: {sellerFilter}</span>}
          </div>
        )}
      </div>

      {selectedProductIds.length > 0 && (
        <div className="selection-bar">
          <span className="selection-count">
            <strong>{selectedProductIds.length}</strong> product(s) selected
          </span>
          <button className="btn btn-link btn-sm" onClick={() => setState({ selectedProductIds: [] })}>
            Clear Selection
          </button>
        </div>
      )}

      <div className="select-all-row">
        <input
          type="checkbox"
          id="selectAll"
          checked={
            paginatedProducts.length > 0 &&
            paginatedProducts.every((p) => selectedProductIds.includes(p.id))
          }
          onChange={handleSelectAll}
        />
        <label htmlFor="selectAll">Select All on Page</label>
      </div>

      <div className="product-list">
        {paginatedProducts.length === 0 ? (
          <div className="empty-state">
            <i className="fas fa-box-open"></i>
            <p>No products found matching your filters.</p>
          </div>
        ) : (
          paginatedProducts.map((product) => (
            <div
              key={product.id}
              className={`product-card ${selectedProductIds.includes(product.id) ? "selected" : ""}`}
            >
              <div className="product-main">
                <div className="product-main-content">
                  <div className="product-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedProductIds.includes(product.id)}
                      onChange={() => handleSelectProduct(product.id)}
                    />
                  </div>

                  <img src={product.images[0]} alt={product.title} className="product-image" />

                  <div className="product-info">
                    <h5 className="product-title">{product.title}</h5>
                    <p className="product-description">{product.description}</p>

                    <div className="product-badges">
                      <span className="product-badge">
                        Price:{" "}
                        {isOwnerOrAdmin
                          ? `$${Number(product.price || 0).toFixed(2)}`
                          : formatDisplayPrice(product.price, displayPricePercentage, { prefix: "$" })}
                      </span>
                      <span className="product-badge">
                        Category: {product.category || "Uncategorized"}
                      </span>
                      <span className="product-badge">
                        Condition:{" "}
                        {product.condition ||
                          product.marketplaceDetails?.ebay?.Condition ||
                          "Not specified"}
                      </span>
                      <span className="product-badge">Quantity: {product.quantity || 0}</span>
                      {product.uploadedBy?.name && (
                        <span className="product-badge">Seller: {product.uploadedBy.name}</span>
                      )}
                    </div>

                    <div className="marketplace-badges">
                      {product.selectedMarketplaces.map((id) => {
                        const market = marketplaces.find((m) => m.id === id);
                        if (id === "local") return null;
                        return (
                          <span key={id} className="marketplace-badge">
                            {market?.name || id}
                          </span>
                        );
                      })}
                    </div>
                  </div>

                  <div className="product-actions">
                    <button
                      className="btn btn-info btn-sm product-action-btn"
                      onClick={() => handleToggleExpand(product.id)}
                    >
                      {expandedProduct === product.id ? "Collapse" : "Expand"}
                    </button>

                    <button
                      className="btn btn-danger btn-sm product-action-btn"
                      onClick={() =>
                        navigate(`/products/${product.id}`, { state: { product } })
                      }
                    >
                      View Details
                    </button>

                    <button
                      className="btn btn-warning btn-sm product-action-btn"
                      onClick={() => navigate(`/products/update/${product.id}`)}
                    >
                      Update
                    </button>

                    <button className="btn btn-dark btn-sm product-action-btn">Delete</button>

                    <button
                      className="btn btn-success btn-sm product-action-btn"
                      onClick={() =>
                        navigate("/products/add", { state: { cloneProduct: product } })
                      }
                    >
                      Add Product
                    </button>
                  </div>
                </div>
              </div>

              {expandedProduct === product.id && (
                <div className="expanded-details">
                  {product.selectedMarketplaces.map((marketplaceId) => {
                    const market = marketplaces.find((m) => m.id === marketplaceId);
                    const details = product.marketplaceDetails[marketplaceId];
                    if (!details || Object.keys(details).length === 0) return null;

                    return (
                      <div key={marketplaceId} className="marketplace-details-section">
                        <h6 className={`marketplace-title text-${market?.color || "secondary"}`}>
                          {market?.name || marketplaceId.toUpperCase()} Details
                        </h6>
                        <div className="details-grid">
                          {Object.entries(details)
                            .filter(
                              ([, value]) =>
                                value &&
                                value !== "" &&
                                value !== "Not specified" &&
                                value !== "Not listed" &&
                                value !== "Not synced"
                            )
                            .map(([key, value]) => (
                              <div key={key} className="detail-item">
                                <span className="detail-label">{key}</span>
                                <span className="detail-value">
                                  {typeof value === "object" ? JSON.stringify(value) : String(value)}
                                </span>
                              </div>
                            ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {totalPages > 1 && (
        <>
          <div className="pagination">
            <div className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
              <button className="page-link" onClick={() => handlePageChange(currentPage - 1)}>
                Previous
              </button>
            </div>

            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }

              return (
                <div key={pageNum} className={`page-item ${currentPage === pageNum ? "active" : ""}`}>
                  <button className="page-link" onClick={() => handlePageChange(pageNum)}>
                    {pageNum}
                  </button>
                </div>
              );
            })}

            <div className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
              <button className="page-link" onClick={() => handlePageChange(currentPage + 1)}>
                Next
              </button>
            </div>
          </div>
          <div className="pagination-info">
            Page {currentPage} of {totalPages} ({filteredProducts.length} items)
          </div>
        </>
      )}
    </MDBContainer>
  );
};

export default ViewProductsPage;